import { useWorkspaceTab } from '@components/Common/EntityWorkspace/useWorkspaceTab';
import { ROLE_OPTIONS, describeRole, formatRole } from '@components/Common/Utils/roles';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import ConfirmDialog from '@components/Common/ConfirmDialog';
import CRMStatusChip from '@components/Common/CRMStatusChip';
import {
  ActivityRail,
  EntityWorkspace,
  IdentityRail,
  SectionCard,
  WorkspaceField,
} from '@components/Common/EntityWorkspace';
import RecordActivityCard from '@components/Common/EntityWorkspace/RecordActivityCard';
import type { GlanceRow, TimelineEntry, WorkspaceTab } from '@components/Common/EntityWorkspace';
import { useActivityRail } from '@components/Common/EntityWorkspace/useActivityRail';
import ErrorMessage from '@components/Common/ErrorMessage';
import SelectFormField from '@components/Common/SelectFormField';
import { useToast } from '@components/Common/Toast/ToastProvider';
import WorkspaceInput from '@components/Common/WorkspaceInput';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import HistoryIcon from '@mui/icons-material/History';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import { Box, Button, FormControlLabel, Switch } from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { AdminUser, UserRole } from './index';
import { formatDate } from '@helpers/date';

/**
 * An internal user, as an entity workspace.
 *
 * This route used to render the create form in permanent edit mode — the
 * DetailPageHeader/DetailSection pattern every other record has moved off.
 *
 * The role values are the RBAC contract and are not touched here: SUPER_ADMIN
 * and HR are what the server reads, and only the labels are friendly.
 */

const TABS: WorkspaceTab[] = [
  { id: 'profile', label: 'Profile', icon: <PersonOutlineIcon /> },
  { id: 'security', label: 'Security', icon: <LockOutlinedIcon /> },
  { id: 'history', label: 'History', icon: <HistoryIcon /> },
];


const roleLabel = (role?: string | null) =>
  ROLE_OPTIONS.find((o) => o.value === role)?.label ?? formatRole(role);

const initialsOf = (first?: string, last?: string) =>
  `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase() || 'U';

const fmt = (d?: string) => formatDate(d);

interface Form {
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole;
  isActive: boolean;
}

const UserWorkspace: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [user, setUser] = useState<AdminUser | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* In the URL, not in state: a tab is a place, and this one was unshareable and did
     not survive a reload. See useWorkspaceTab. */
  const [activeTab, setActiveTab] = useWorkspaceTab(TABS, 'profile');
  const [editing, setEditing] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const snapshot = useRef<Form | null>(null);
  const [activityOpen, toggleActivity] = useActivityRail();

  // Password is deliberately not part of `form`: it is write-only, never
  // round-trips from the server, and must not be carried by a section save.
  const [password, setPassword] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`/api/users/${id}`);
      const record: AdminUser = {
        _id: data._id,
        firstName: data.personalDetails?.firstName ?? '',
        lastName: data.personalDetails?.lastName ?? '',
        fullName:
          data.personalDetails?.fullName
          || `${data.personalDetails?.firstName ?? ''} ${data.personalDetails?.lastName ?? ''}`.trim(),
        email: data.personalDetails?.email ?? '',
        // No default. Pre-filling HR for a user with no role meant one Save demoted an account the
        // login controller treats as Super Admin; now the form makes the admin choose.
        role: (data.role ?? '') as UserRole,
        isActive: data.isActive !== false,
        createdAt: data.createdAt ?? '',
      };
      setUser(record);
      setForm({
        firstName: record.firstName,
        lastName: record.lastName,
        email: record.email,
        role: record.role as UserRole,
        isActive: record.isActive,
      });
    } catch {
      setError('Failed to load the user. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (userId) load(userId);
  }, [userId, load]);

  const setField = <K extends keyof Form>(key: K, value: Form[K]) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
    setDirty(true);
  };

  const startEdit = (id: string) => {
    if (editing && editing !== id && dirty) return; // one section at a time
    snapshot.current = form ? { ...form } : null;
    setEditing(id);
    setDirty(false);
  };

  const cancelEdit = () => {
    if (snapshot.current) setForm(snapshot.current);
    setPassword('');
    setEditing(null);
    setDirty(false);
  };

  /**
   * One save path. The password is only sent when one was typed — an empty
   * field means "keep the current one", exactly as the old form did.
   */
  const saveSection = async () => {
    if (!form || !userId) return;
    if (!form.firstName.trim()) return showToast('First name is required', 'error');
    if (!form.lastName.trim()) return showToast('Last name is required', 'error');
    if (!/\S+@\S+\.\S+/.test(form.email.trim())) return showToast('A valid email is required', 'error');
    if (!form.role) return showToast('Role is required', 'error');

    setSaving(true);
    try {
      const payload: Record<string, unknown> = { ...form };
      if (password) payload.password = password;
      await axios.put(`/api/users/${userId}`, payload);
      showToast(password ? 'User updated and password reset' : 'User updated', 'success');
      setPassword('');
      setEditing(null);
      setDirty(false);
      await load(userId);
    } catch {
      showToast('Failed to save the user', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!userId) return;
    setSaving(true);
    try {
      await axios.delete(`/api/users/${userId}`);
      setConfirmDelete(false);
      navigate('/user-rbac');
    } catch (err: any) {
      // The server refuses some deletions (the last super admin, yourself);
      // its reason is the useful part, so it is surfaced rather than replaced.
      showToast(err?.response?.data?.message ?? 'Failed to delete the user', 'error');
      setConfirmDelete(false);
    } finally {
      setSaving(false);
    }
  };

  if (loading && !user) return <SkeletonLoader variant="workspace" />;
  if (error && !user) return <ErrorMessage page message={error} />;
  if (!user || !form) return null;

  const isEditing = (id: string) => editing === id;
  const input = (key: 'firstName' | 'lastName' | 'email', type = 'text') => (
    <WorkspaceInput type={type} value={form[key]} onChange={(v) => setField(key, v)} />
  );

  const glance: GlanceRow[] = [
    { key: 'Role', value: roleLabel(form.role) },
    { key: 'Email', value: form.email || '-', mono: true },
    { key: 'Joined', value: fmt(user.createdAt), mono: true },
  ];

  const identityRail = (
    <IdentityRail
      initials={initialsOf(form.firstName, form.lastName)}
      name={`${form.firstName} ${form.lastName}`.trim() || 'User'}
      subId={user._id}
      statusChip={<CRMStatusChip status={form.isActive ? 'active' : 'inactive'} />}
      glance={glance}
      audit={{ createdAt: user.createdAt }}
    />
  );

  const profile = (
    <>
      <SectionCard
        title="Personal details"
        icon={<PersonOutlineIcon />}
        meta="3 fields"
        editing={isEditing('personal')}
        onEdit={() => startEdit('personal')}
        onCancel={cancelEdit}
        onSave={saveSection}
      >
        <WorkspaceField label="First name" value={form.firstName} editing={isEditing('personal')} edit={input('firstName')} />
        <WorkspaceField label="Last name" value={form.lastName} editing={isEditing('personal')} edit={input('lastName')} />
        <WorkspaceField label="Email" value={form.email} mono editing={isEditing('personal')} edit={input('email', 'email')} />
      </SectionCard>

      <SectionCard
        title="Access and role"
        icon={<SecurityIcon />}
        editing={isEditing('access')}
        onEdit={() => startEdit('access')}
        onCancel={cancelEdit}
        onSave={saveSection}
      >
        <WorkspaceField
          label="Role"
          value={roleLabel(form.role)}
          editing={isEditing('access')}
          edit={(
            <SelectFormField
              required={false}
              name="role"
              label=""
              value={form.role}
              options={ROLE_OPTIONS}
              onChange={(_n, v) => setField('role', v as UserRole)}
            />
          )}
        />
        <WorkspaceField
          label="Account status"
          value={<CRMStatusChip status={form.isActive ? 'active' : 'inactive'} />}
          editing={isEditing('access')}
          edit={(
            <FormControlLabel
              control={(
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setField('isActive', e.target.checked)}
                />
              )}
              label={form.isActive ? 'Active' : 'Inactive'}
            />
          )}
        />
        <WorkspaceField
          label="What this role can do"
          full
          value={describeRole(form.role)}
        />
      </SectionCard>

      <SectionCard title="Danger zone" icon={<DeleteOutlineIcon />}>
        <Box className="cc-user__danger">
          <Button variant="outlined" color="error" size="small" onClick={() => setConfirmDelete(true)}>
            Delete user
          </Button>
          <span className="cc-user__danger-note">
            Permanent. Deactivating instead keeps the record and its history, and
            stops them signing in.
          </span>
        </Box>
      </SectionCard>
    </>
  );

  const security = (
    <SectionCard
      title="Password"
      icon={<LockOutlinedIcon />}
      editing={isEditing('password')}
      onEdit={() => startEdit('password')}
      onCancel={cancelEdit}
      onSave={saveSection}
    >
      <WorkspaceField
        label="Password"
        full
        value="Set. Only this user knows it."
        editing={isEditing('password')}
        edit={(
          <WorkspaceInput
            type="password"
            autoComplete="new-password"
            placeholder="New password"
            helperText="Leave blank to keep the current password."
            value={password}
            onChange={(v) => {
              setPassword(v);
              setDirty(true);
            }}
          />
        )}
      />
    </SectionCard>
  );

  const timeline: TimelineEntry[] = user.createdAt
    ? [{
      id: 'created',
      kind: 'submitted',
      text: 'Account created',
      meta: fmt(user.createdAt),
    }]
    : [];

  const history = (
      <>
      <SectionCard title="Record activity" icon={<HistoryIcon />}>
        <WorkspaceField label="Created" value={fmt(user.createdAt)} mono />
        <WorkspaceField label="User ID" value={user._id} mono />
      </SectionCard>
        <RecordActivityCard recordId={user._id} />
      </>
  );

  const tabContent: Record<string, React.ReactNode> = { profile, security, history };

  const SECTION_LABELS: Record<string, string> = {
    personal: 'Personal details',
    access: 'Access and role',
    password: 'Password',
  };

  return (
    <>
      <EntityWorkspace
        backLabel="Users"
        onBack={() => navigate('/user-rbac')}
        identityRail={identityRail}
        tabs={TABS}
        activeTabId={activeTab}
        onTabChange={setActiveTab}
        activityRail={<ActivityRail timeline={timeline} />}
        activityOpen={activityOpen}
        onToggleActivity={toggleActivity}
        isDirty={dirty && !!editing}
        dirtySectionLabel={editing ? SECTION_LABELS[editing] ?? 'Section' : undefined}
        onDiscard={cancelEdit}
        onSave={saveSection}
      >
        {tabContent[activeTab]}
      </EntityWorkspace>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this user?"
        body={(
          <>
            This removes {`${form.firstName} ${form.lastName}`.trim() || 'the user'} and
            their access permanently. Anything they created stays.
          </>
        )}
        confirmText={form.email || 'DELETE'}
        confirmLabel={form.email ? 'Email' : 'Type DELETE'}
        actionLabel="Delete user"
        busy={saving}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default UserWorkspace;
